import React, { useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

import DOMPurify from 'dompurify'

import validation from '../../../validation'
import AddButton from '../../controls/AddButton'
import CheckBox from '../../controls/CheckBox'
import DatePicker from '../../controls/DatePicker'
import RemoveButton from '../../controls/RemoveButton'
import TextArea from '../../controls/TextArea'

export default {
  name: 'MMI',
  group: 'Case Details',
  sectionNumber: 4.5,
  validationSchema: getValidationSchema(),
  Component,
}

function getValidationSchema() {
  return validation.object({
    mmi: validation.object({
      history: validation
        .arrayOf(
          validation.object({
            date: validation.date('Date').when('narrative', {
              is: val => !val || val.trim() === '',
              then: validation.date('Date').required('Date is required when narrative is empty.'),
              otherwise: validation.date('Date').nullable(),
            }),
            narrative: validation.richText('Narrative'),
            isActual: validation.boolean(),
          }),
        )
        .nullable(),
    }),
  })
}

function Component({
  addItemToArray,
  removeItemFromArray,
  createInputProps,
  getValue,
  setModelValue,
  setModelValues,
  ErrorMessage,
}) {
  const [editingIndex, setEditingIndex] = useState(null)
  const history = getValue('mmi.history', [])

  const handleActualChange = (index, checked) => {
    // If checking this one, uncheck all others
    if (checked) {
      const newHistory = history.map((item, i) => ({
        ...item,
        isActual: i === index,
      }))
      setModelValue('mmi.history', newHistory)
    } else {
      setModelValue(`mmi.history[${index}].isActual`, false)
    }
  }

  const handleAddEntry = () => {
    const nextIndex = history.length
    addItemToArray('mmi.history', { isActual: false })
    setEditingIndex(nextIndex)
  }

  const handleRemove = index => {
    const newHistory = [...history.slice(0, index), ...history.slice(index + 1)]
    if (newHistory.length === 0) {
      if (setModelValues) {
        setModelValues([
          ['mmi.history', []],
          ['mmi.date', null],
          ['mmi.mmiDate', null],
          ['mmi.reached', null],
          ['mmi.isMmiReached', null],
          ['mmi.impairmentRating', null],
        ])
      } else {
        removeItemFromArray('mmi.history', index)
        if (setModelValue) {
          setModelValue('mmi.date', null)
          setModelValue('mmi.mmiDate', null)
          setModelValue('mmi.reached', null)
          setModelValue('mmi.isMmiReached', null)
          setModelValue('mmi.impairmentRating', null)
        }
      }
    } else {
      removeItemFromArray('mmi.history', index)
    }
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  return (
    <div className="MMISection-component">
      <h4 className="pb-3">
        Maximum Medical Improvement (MMI){' '}
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip id="mmi-tooltip">
              Maximum Medical Improvement (MMI) is the point in a person&apos;s recovery from an injury or illness where
              their condition is not expected to significantly improve with further medical treatment. It essentially
              means the patient&apos;s condition has plateaued, and no further medical or rehabilitative treatments are
              likely to result in substantial improvement.
            </Tooltip>
          }
        >
          <i className="fa fa-question-circle" style={{ fontSize: '1rem', cursor: 'pointer', color: '#2f5496' }} />
        </OverlayTrigger>
      </h4>
      {history.map((x, index) => {
        const namePrefix = `mmi.history[${index}]`
        const isEditing = editingIndex === index
        return (
          <React.Fragment key={namePrefix}>
            <MMIEntry
              namePrefix={namePrefix}
              index={index}
              item={x}
              createInputProps={createInputProps}
              getValue={getValue}
              ErrorMessage={ErrorMessage}
              handleRemove={() => handleRemove(index)}
              onActualChange={handleActualChange}
              isEditing={isEditing}
              onEdit={() => setEditingIndex(index)}
              onDone={() => setEditingIndex(null)}
            />
            <hr />
          </React.Fragment>
        )
      })}
      <AddButton id="addMMIButton" onClick={handleAddEntry}>
        Add MMI Entry
      </AddButton>
    </div>
  )
}

function MMIEntry({
  namePrefix,
  index,
  item,
  createInputProps,
  getValue,
  ErrorMessage,
  handleRemove,
  onActualChange,
  isEditing,
  onEdit,
  onDone,
}) {
  const date = getValue(`${namePrefix}.date`, item?.date || '')
  const narrative = getValue(`${namePrefix}.narrative`, item?.narrative || '')
  const isActual = item?.isActual ?? getValue(`${namePrefix}.isActual`, false)
  const hasNarrative = Boolean(narrative && narrative.replace(/<[^>]*>/g, '').trim())

  if (!isEditing) {
    return (
      <div className="d-flex align-items-start justify-content-between clearfix">
        <div className="w-100 mr-3">
          <div>
            <span className="font-weight-semibold">{date || <em className="text-muted">No date specified</em>}</span>
            {isActual ? (
              <span className="badge badge-success ml-3">Actual Date of MMI</span>
            ) : (
              <span className="badge badge-secondary ml-3">Estimated</span>
            )}
          </div>
          {hasNarrative && (
            <div
              className="text-muted mt-1"
              style={{ fontSize: '0.85em' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(narrative) }}
            />
          )}
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onEdit} title="Edit this MMI entry">
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    )
  }

  return (
    <div className="clearfix">
      <div className="form-row">
        <div className="form-group col-md-4">
          <label className="form-label" htmlFor={`${namePrefix}.date`}>
            Date
          </label>
          <DatePicker {...createInputProps(`${namePrefix}.date`)} />
          <ErrorMessage name={`${namePrefix}.date`} />
        </div>

        <div className="form-group col-md-8">
          <div className="form-check mt-4 pt-2">
            <CheckBox
              id={`${namePrefix}.isActual`}
              name={`${namePrefix}.isActual`}
              checked={isActual || false}
              onChange={e => onActualChange(index, e.target.checked)}
            >
              Actual Date of MMI (vs Estimated)
            </CheckBox>
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group col-md-12">
          <label className="form-label" htmlFor={`${namePrefix}.narrative`}>
            MMI Narrative
          </label>
          <TextArea
            areaProps={{ ...createInputProps(`${namePrefix}.narrative`) }}
            rows={3}
            maxLength={validation.maxLengths.richText}
          />
          <ErrorMessage name={`${namePrefix}.narrative`} />
        </div>
      </div>

      <div className="form-row mb-2 mt-2">
        <div className="col-12 d-flex align-items-end justify-content-end pb-1" style={{ gap: '8px' }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onDone}>
            Done
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    </div>
  )
}
